/**
 * ONE POLICY PER QUANTITY, and every surface goes through it.
 *
 * The same number was being written four ways depending on where it landed: a
 * session length read `5m 46s` on a card, `167m` on the axis beside it and
 * `6m 59s` in the tooltip over that axis; a share read `21%`, `2.1%`, `70%`
 * and `78.2%` on adjacent panels; a count read `18,452` on a card and `5k` on
 * an axis two inches away. Each was individually defensible and together they
 * read as four different products.
 *
 * So the UNIT decides the format, not the call site. Every quantity comes in
 * two forms and no more:
 *
 *   `x()`      the VALUE — what a person would quote. Never abbreviated.
 *   `xAxis()`  the TICK — abbreviated, because an axis is scaffolding and a
 *              y-axis reading 25,000 costs width the chart needs. Never used
 *              for anything a reader might repeat.
 *
 * Anything that formats a number outside this file is a fifth way of writing
 * it.
 */

/** `null` is an em dash everywhere, and never a zero. */
const NONE = "—";

function isNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

// ------------------------------------------------------------- durations

/**
 * A duration a person can read aloud, always with two parts.
 *
 * BOTH PARTS EVEN WHEN ONE IS ZERO — `3m 0s`, not `3m`. A column showing "6m"
 * beside "5m 48s" reads as two different kinds of measurement, and the eye
 * cannot scan a column whose shape changes row to row. The cost is one
 * odd-looking value; the benefit is that every duration in the product has the
 * same silhouette.
 */
export function duration(seconds: number | null | undefined): string {
  if (!isNumber(seconds)) return NONE;
  const whole = Math.max(0, Math.round(seconds));
  if (whole < 60) return `${whole}s`;
  const minutes = Math.floor(whole / 60);
  if (minutes < 60) return `${minutes}m ${whole % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * A duration as an axis tick: ONE unit, chosen from the magnitude.
 *
 * `7m` rather than `6m 59s`, because an axis label is read for its position on
 * a scale and not for its precision — eight ticks of `Xm Ys` is a wall of
 * digits down the left edge of every chart.
 */
export function durationAxis(seconds: number): string {
  if (!isNumber(seconds)) return "";
  const whole = Math.max(0, Math.round(seconds));
  if (whole === 0) return "0";
  if (whole < 60) return `${whole}s`;
  const minutes = Math.round(whole / 60);
  if (minutes < 90) return `${minutes}m`;
  const hours = whole / 3600;
  return hours < 10 ? `${hours.toFixed(1)}h` : `${Math.round(hours)}h`;
}

// ---------------------------------------------------------------- shares

/**
 * A share, with the precision its MAGNITUDE deserves rather than the precision
 * the call site happened to pass.
 *
 * Below 10% a whole number throws away most of what there is to see — payer
 * conversion moving 2.1% to 2.8% is a third more revenue and reads as "2% to
 * 3%". At or above 10% the decimal is noise, and `78.2%` invites a reader to
 * treat a tenth of a point as a signal. One rule everywhere, so two panels can
 * be compared without checking how each was rounded.
 */
export function percent(value: number | null | undefined): string {
  if (!isNumber(value)) return NONE;
  const scaled = value * 100;
  if (scaled !== 0 && Math.abs(scaled) < 10) return `${scaled.toFixed(1)}%`;
  return `${Math.round(scaled)}%`;
}

/** Rate comparisons need stable hundredth-of-a-percentage-point precision. */
export function precisePercent(value: number | null | undefined): string {
  return isNumber(value) ? `${(value * 100).toFixed(2)}%` : NONE;
}

export const retentionPercent = precisePercent;

/** The same rule on an axis, where the axis's RANGE decides, not each tick. */
export function percentAxis(value: number, ceiling: number): string {
  return ceiling <= 0.12
    ? `${(value * 100).toFixed(1)}%`
    : `${Math.round(value * 100)}%`;
}

// ---------------------------------------------------------------- counts

/** A count. Grouped, never abbreviated — "1.2k players" loses the 200. */
export function count(value: number | null | undefined): string {
  if (!isNumber(value)) return NONE;
  return Math.round(value).toLocaleString();
}

/**
 * A count as an axis tick. Abbreviated only past ten thousand, where the last
 * three digits are certainly not what the reader is checking.
 */
export function countAxis(value: number): string {
  if (!isNumber(value)) return "";
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000)
    return `${(value / 1_000_000).toFixed(magnitude >= 10_000_000 ? 0 : 1)}M`;
  if (magnitude >= 10_000) return `${Math.round(value / 1000)}k`;
  return Math.round(value).toLocaleString();
}

/**
 * A rate that is not a share — sessions per player, players per server. One
 * decimal: the whole number alone loses the difference between 1.2 and 1.9,
 * and a second decimal is beyond what the sample supports.
 */
export function ratio(value: number | null | undefined, decimals = 1): string {
  if (!isNumber(value)) return NONE;
  return value.toFixed(decimals);
}

/**
 * A measured quantity with a unit suffix — fps, ms, MB. One decimal, for the
 * same reason `ratio` has one: a server averaging 59.833333333333336 fps is
 * not more precise than 59.8, only longer.
 */
export function measure(
  value: number | null | undefined,
  unit: string,
): string {
  if (!isNumber(value)) return NONE;
  return `${Math.round(value * 10) / 10}${unit}`;
}

/**
 * A DATABASE KEY, MADE READABLE — `match_reward` becomes "Match reward".
 *
 * These are identifiers the GAME chose, and they were reaching the screen
 * verbatim: `daily_login`, `shop_weapon`, `currency_earn`, `skip-battle-pass-5`.
 * A reader who did not write the game cannot tell `shop_weapon` from
 * `shop_cosmetic` at a glance, and the underscore is a tell that nobody
 * translated the data into a product.
 *
 * DELIBERATELY NOT A LOOKUP TABLE. Every one of these names is invented by the
 * game and there is no registration step, so a mapping would be permanently
 * incomplete and would silently show the raw key for anything new — the worst
 * of both. Separators become spaces and the first letter is capitalised, which
 * is right for every name a game will plausibly choose and wrong for none.
 *
 * A trailing number is kept attached (`skill-12` reads "Skill 12"), because it
 * is usually the only thing distinguishing one from the next.
 */
export function humanKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (spaced === "") return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
